import { useCallback, useState } from 'react';
import { Header, Page } from 'redesign';
import { BulkEditSidebar } from 'shared/components/bulk/bulk-shift-action/bulk-edit-sidebar';
import { BulkArchiveSidebar } from 'shared/components/bulk/bulk-shift-action/bulk-archive-sidebar';
import { WorkFormSidebar } from 'shared/components/work-forms/work-form-sidebar';
import { FeatureFlagProvider } from 'shared/contexts/feature-flag-provider';
import { useGetCompanies } from 'hq/hooks/queryHooks/companies/use-get-companies';
import { BulkForwardToPartnerSidebar } from 'shared/components/bulk/bulk-shift-action/bulk-forward-sidebar';
import { ShiftPlanningTableDataProvider } from './table/shift-planning-table';
import { useShiftPlanningPage } from './table/hooks/use-shift-planning';
import { ShiftPlanningDataProvider } from './filters/shift-planning-filters-data-provider';
import { ShiftPlanningToolbox } from './shift-planning-toolbox';
import { useToggle } from '../../../../../shared/hooks';

type SidebarState =
  | 'create'
  | 'edit'
  | 'duplicate'
  | 'bulk-edit'
  | 'bulk-archive'
  | 'bulk-forward'
  | null;

const ShiftPlanning = () => {
  const [sidebarState, setSidebarState] = useState<SidebarState>(null);
  const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);
  const { on: filtersOpen, toggle: toggleFilters } = useToggle();
  const {
    filters: appliedFilters,
    setFilters,
    selectedFilterCount,
    setShiftDetailId,
  } = useShiftPlanningPage();
  const companiesQuery = useGetCompanies();

  const handleSelectionChange = useCallback((ids: string[]) => {
    setSelectedShiftIds(ids);
  }, []);

  const handleShiftAction = useCallback((action: 'edit' | 'duplicate', shiftId: string) => {
    setSelectedShiftIds([shiftId]);
    setSidebarState(action);
  }, []);

  return (
    <Page
      header={<Header titleText="Shift Planning" />}
      toolbox={
        <ShiftPlanningToolbox
          selectedFilterCount={selectedFilterCount}
          selectedShiftIds={selectedShiftIds}
          setCreateWorkSidebarOpen={() => setSidebarState('create')}
          setIsBulkForwardToPartnerOpen={() => setSidebarState('bulk-forward')}
          setIsBarSideBarOpen={() => setSidebarState('bulk-edit')}
          setIsBulkArchiveBarOpen={() => setSidebarState('bulk-archive')}
          toggleFilters={toggleFilters}
        />
      }
    >
      <FeatureFlagProvider>
        <ShiftPlanningDataProvider
          appliedFilters={appliedFilters}
          handleFiltersChange={setFilters}
          isOpen={filtersOpen}
          toggleFilters={toggleFilters}
        />
      </FeatureFlagProvider>

      {(sidebarState === 'create' || sidebarState === 'edit' || sidebarState === 'duplicate') && (
        <WorkFormSidebar
          isSidebarOpen={true}
          closeSidebar={() => setSidebarState(null)}
          companies={companiesQuery.data ?? []}
          formMode={sidebarState}
          selectedShiftIds={selectedShiftIds}
          isHqAdmin={true}
          onSuccess={setShiftDetailId}
        />
      )}
      {sidebarState === 'bulk-edit' && (
        <BulkEditSidebar
          selectedShiftIds={selectedShiftIds}
          isBulkSideBarOpen={sidebarState === 'bulk-edit'}
          setIsBarSideBarOpen={() => setSidebarState(null)}
        />
      )}
      {sidebarState === 'bulk-forward' && (
        <BulkForwardToPartnerSidebar
          selectedShiftIds={selectedShiftIds}
          isBulkSideBarOpen={sidebarState === 'bulk-forward'}
          setIsBarSideBarOpen={() => setSidebarState(null)}
        />
      )}
      {sidebarState === 'bulk-archive' && (
        <BulkArchiveSidebar
          selectedShiftIds={selectedShiftIds}
          isBulkArchiveBarOpen={sidebarState === 'bulk-archive'}
          setIsBarSideBarOpen={() => setSidebarState(null)}
        />
      )}
      <ShiftPlanningTableDataProvider
        selectedShiftIds={selectedShiftIds}
        onSelectionChange={handleSelectionChange}
        setShiftAction={handleShiftAction}
      />
    </Page>
  );
};

export { ShiftPlanning };
